"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StoreManager = void 0;
const utils_1 = require("@streamr/utils");
const identifiers_1 = require("../../identifiers");
const timestamp_1 = require("../../../generated/google/protobuf/timestamp");
const DhtRpc_1 = require("../../../generated/packages/dht/protos/DhtRpc");
const getClosestNodes_1 = require("../contact/getClosestNodes");
const StoreRpcLocal_1 = require("./StoreRpcLocal");
const logger = new utils_1.Logger(module);
class StoreManager {
    options;
    constructor(options) {
        this.options = options;
        this.registerLocalRpcMethods();
    }
    registerLocalRpcMethods() {
        const rpcLocal = new StoreRpcLocal_1.StoreRpcLocal({
            localPeerDescriptor: this.options.localPeerDescriptor,
            localDataStore: this.options.localDataStore,
            replicateDataToContact: (dataEntry, contact) => this.replicateDataToContact(dataEntry, contact),
            getStorers: (dataKey) => this.getStorers(dataKey)
        });
        this.options.rpcCommunicator.registerRpcMethod(DhtRpc_1.StoreDataRequest, DhtRpc_1.StoreDataResponse, 'storeData', (request) => rpcLocal.storeData(request));
        this.options.rpcCommunicator.registerRpcNotification(DhtRpc_1.ReplicateDataRequest, 'replicateData', (request, context) => rpcLocal.replicateData(request, context));
    }
    onContactAdded(peerDescriptor) {
        for (const key of this.options.localDataStore.keys()) {
            this.replicateAndUpdateStaleState(key, peerDescriptor);
        }
    }
    replicateAndUpdateStaleState(dataKey, newNode) {
        const storers = this.getStorers(dataKey);
        const storersBeforeContactAdded = storers.filter((p) => !(0, identifiers_1.areEqualPeerDescriptors)(p, newNode));
        const selfWasPrimaryStorer = (0, identifiers_1.areEqualPeerDescriptors)(storersBeforeContactAdded[0], this.options.localPeerDescriptor);
        if (selfWasPrimaryStorer) {
            if (storers.some((p) => (0, identifiers_1.areEqualPeerDescriptors)(p, newNode))) {
                setImmediate(async () => {
                    const dataEntries = Array.from(this.options.localDataStore.values(dataKey));
                    await Promise.all(dataEntries.map(async (dataEntry) => this.replicateDataToContact(dataEntry, newNode)));
                });
            }
        }
        else if (!storers.some((p) => (0, identifiers_1.areEqualPeerDescriptors)(p, this.options.localPeerDescriptor))) {
            this.options.localDataStore.setAllEntriesAsStale(dataKey);
        }
    }
    async replicateDataToContact(dataEntry, contact) {
        const rpcRemote = this.options.createRpcRemote(contact);
        try {
            await rpcRemote.replicateData({ entry: dataEntry }, true);
        }
        catch (e) {
            logger.trace('replicateData() threw an exception ' + e);
        }
    }
    async storeDataToDht(key, data, creator) {
        logger.debug(`Storing data to DHT ${this.options.serviceId}`);
        const result = await this.options.recursiveOperationManager.execute(key, DhtRpc_1.RecursiveOperation.FIND_CLOSEST_NODES);
        const closestNodes = result.closestNodes;
        const successfulNodes = [];
        const ttl = this.options.highestTtl; // ToDo: make TTL decrease according to some nice curve
        const createdAt = timestamp_1.Timestamp.now();
        for (let i = 0; i < closestNodes.length && successfulNodes.length < this.options.redundancyFactor; i++) {
            const keyRaw = (0, identifiers_1.toDhtAddressRaw)(key);
            const creatorRaw = (0, identifiers_1.toDhtAddressRaw)(creator);
            if ((0, identifiers_1.areEqualPeerDescriptors)(this.options.localPeerDescriptor, closestNodes[i])) {
                this.options.localDataStore.storeEntry({
                    key: keyRaw,
                    data,
                    creator: creatorRaw,
                    createdAt,
                    storedAt: timestamp_1.Timestamp.now(),
                    ttl,
                    stale: false,
                    deleted: false,
                });
                successfulNodes.push(closestNodes[i]);
                continue;
            }
            const rpcRemote = this.options.createRpcRemote(closestNodes[i]);
            try {
                await rpcRemote.storeData({
                    key: keyRaw,
                    data,
                    creator: creatorRaw,
                    createdAt,
                    ttl
                });
                successfulNodes.push(closestNodes[i]);
                logger.trace('remote.storeData() success');
            }
            catch (e) {
                logger.trace('remote.storeData() threw an exception ' + e);
            }
        }
        return successfulNodes;
    }
    async replicateDataToClosestNodes() {
        const dataEntries = Array.from(this.options.localDataStore.values());
        await Promise.all(dataEntries.map(async (dataEntry) => {
            const dataKey = (0, identifiers_1.toDhtAddress)(dataEntry.key);
            const neighbors = (0, getClosestNodes_1.getClosestNodes)(dataKey, this.options.getNeighbors(), { maxCount: this.options.redundancyFactor });
            await Promise.all(neighbors.map(async (neighbor) => {
                const rpcRemote = this.options.createRpcRemote(neighbor);
                try {
                    await rpcRemote.replicateData({ entry: dataEntry }, false);
                }
                catch (err) {
                    logger.trace('Failed to replicate data in replicateDataToClosestNodes', { err });
                }
            }));
        }));
    }
    getStorers(dataKey, excludedNode) {
        return (0, getClosestNodes_1.getClosestNodes)(dataKey, [...this.options.getNeighbors(), this.options.localPeerDescriptor], {
            maxCount: this.options.redundancyFactor,
            excludedNodeIds: excludedNode !== undefined ? new Set([(0, identifiers_1.toNodeId)(excludedNode)]) : undefined
        });
    }
    async destroy() {
        await this.replicateDataToClosestNodes();
    }
}
exports.StoreManager = StoreManager;
//# sourceMappingURL=StoreManager.js.map